import { useEffect, useState, type ReactNode } from "react";

interface DeferredMountProps {
  children: ReactNode;
  /** Extra ms to wait after idle before mounting. Default 0. */
  delay?: number;
  /** Also mount on first user interaction (pointer/key/scroll). Default true. */
  onInteraction?: boolean;
  /** Hard fallback timeout in ms if idle never fires. Default 4000. */
  fallbackMs?: number;
}

/**
 * Defers mounting non-critical UI (chatbot, command palette, etc.) until the
 * browser is idle or the user interacts. Reduces JS executed before LCP on
 * mobile by keeping those chunks out of the initial paint critical path.
 */
export function DeferredMount({
  children,
  delay = 0,
  onInteraction = true,
  fallbackMs = 4000,
}: DeferredMountProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    const mount = () => {
      if (cancelled) return;
      if (delay > 0) {
        window.setTimeout(() => !cancelled && setReady(true), delay);
      } else {
        setReady(true);
      }
    };

    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const idleHandle = ric
      ? ric(mount, { timeout: fallbackMs })
      : window.setTimeout(mount, Math.min(fallbackMs, 1500));

    const events = onInteraction
      ? (["pointerdown", "keydown", "touchstart", "scroll"] as const)
      : [];
    const onAny = () => mount();
    events.forEach((e) =>
      window.addEventListener(e, onAny, { once: true, passive: true })
    );

    return () => {
      cancelled = true;
      if (ric && typeof (window as any).cancelIdleCallback === "function") {
        (window as any).cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle as unknown as number);
      }
      events.forEach((e) => window.removeEventListener(e, onAny));
    };
  }, [ready, delay, onInteraction, fallbackMs]);

  return ready ? <>{children}</> : null;
}
